// src/services/api/config.ts
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

// Configuration constants
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:7115',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
};

// Custom error class for API errors
export class ApiError extends Error {
  status?: number;
  code?: string;
  details?: any;

  constructor(message: string, status?: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Create axios instance with default configuration
const createApiClient = (): AxiosInstance => {
  console.log('process.env.REACT_APP_API_URL', process.env.REACT_APP_API_URL);
  console.log('API_CONFIG.BASE_URL', API_CONFIG.BASE_URL);
  console.log('API_CONFIG.TIMEOUT', API_CONFIG.TIMEOUT);
  const client = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      // Get token from storage
      const token = localStorage.getItem('org_auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: number;
      };

      // Handle retry logic
      if (
        originalRequest &&
        (!originalRequest._retry ||
          originalRequest._retry < API_CONFIG.RETRY_ATTEMPTS)
      ) {
        originalRequest._retry = (originalRequest._retry || 0) + 1;
        await new Promise((resolve) =>
          setTimeout(resolve, API_CONFIG.RETRY_DELAY * originalRequest._retry!)
        );
        return client(originalRequest);
      }

      // Handle authentication errors
      if (error.response?.status === 401) {
        // Redirect to login or refresh token
        localStorage.removeItem('org_auth_token');
        window.location.href = '/login';
      }

      // Transform error to custom ApiError
      throw new ApiError(
        error.response?.data?.message ||
          error.message ||
          'An unexpected error occurred',
        error.response?.status,
        error.response?.data?.code,
        error.response?.data?.details
      );
    }
  );

  return client;
};

export const apiClient = createApiClient();
